import {
  clearMapFeatures,
  openTool,
  selectFeature,
} from '@app/store/actions.js';
import type { RootState } from '@app/store/store.js';
import { authSetUser } from '@features/auth/model/actions.js';
import { elevationSetSettings } from '@features/elevationChart/model/actions.js';
import { affectsElevationSmoothing } from '@features/elevationChart/model/settingsReducer.js';
import { mapsDisconnect, mapsLoaded } from '@features/myMaps/model/actions.js';
import { createReducer } from '@reduxjs/toolkit';
import {
  type TransportType,
  transportTypeDefs,
} from '@shared/transportTypeDefs.js';
import type { Feature, LineString, Polygon } from 'geojson';
import { createSelector } from 'reselect';
import {
  type Alternative,
  type IsochroneParams,
  type PickMode,
  type RoundtripParams,
  type RoutePoint,
  type RoutingMode,
  routeKey,
  routePlannerAddPoint,
  routePlannerDelete,
  routePlannerRecompute,
  routePlannerRemovePoint,
  routePlannerRestoreSavedRoute,
  routePlannerSetActiveAlternativeIndex,
  routePlannerSetFinish,
  routePlannerSetIsochroneParams,
  routePlannerSetIsochrones,
  routePlannerSetMode,
  routePlannerSetParams,
  routePlannerSetPickMode,
  routePlannerSetPoint,
  routePlannerSetPoints,
  routePlannerSetRenderGeojson,
  routePlannerSetResult,
  routePlannerSetRoundtripParams,
  routePlannerSetSampledGeojson,
  routePlannerSetSavedRoute,
  routePlannerSetStart,
  routePlannerSetTransportType,
  routePlannerSupersedeSavedRoute,
  routePlannerSwapEnds,
  routePlannerToggleItineraryVisibility,
  routePlannerToggleMilestones,
  type SavedRoute,
  type Waypoint,
} from './actions.js';

export interface SampledRouteLine {
  line: Feature<LineString>;
  /**
   * Came with a saved map rather than being sampled here, so it stands for
   * whoever opens that map — a sampled line belongs to the tier it was built
   * for and is dropped when that changes.
   */
  saved: boolean;
}

export interface RoutePlannerCleanResultState {
  alternatives: Alternative[];
  waypoints: Waypoint[];
  activeAlternativeIndex: number;
  timestamp: number | null;
  /**
   * {@link routeKey} of what the result on screen was computed for. An edit
   * doesn't clear the result while the next route is in flight, so this is the
   * only thing that says which waypoints the drawn line actually joins.
   */
  resultKey: string | null;
  isochrones: Feature<Polygon>[] | null;
  // DEM-sampled line for the active alternative: every elevation from our DEM
  // (router elevation ignored), long segments densified. What a saved map
  // stores, and what the render line below is built from. Cleared with the
  // result or on alternative switch.
  sampledGeojson: SampledRouteLine | null;
  // Render-only line: the sampled one with bridges/tunnels levelled and
  // elevation smoothed to the live preferences. A cache for the chart and
  // elevation colorize; `null` falls back to the alternative's own coordinates.
  // Never exported.
  renderGeojson: Feature<LineString> | null;
}

export interface RoutePlannerCleanState extends RoutePlannerCleanResultState {
  /**
   * The route the open map has stored. Deliberately outside `clearResult`: an
   * edit, or a request that failed, must not lose the map's own answer — coming
   * back to the waypoints it names puts it on screen again, with no request.
   * Only deleting the route, or asking for a recompute, gives it up.
   */
  savedRoute: SavedRoute | null;
  points: RoutePoint[];
  finishOnly: boolean;
  pickMode: PickMode | null;
  itineraryIsVisible: boolean;
  roundtripParams: RoundtripParams;
  isochroneParams: IsochroneParams;
  hash?: string;
}

const clearResult: RoutePlannerCleanResultState = {
  alternatives: [],
  waypoints: [],
  activeAlternativeIndex: 0,
  timestamp: null,
  resultKey: null,
  isochrones: null,
  sampledGeojson: null,
  renderGeojson: null,
};

export const cleanState: RoutePlannerCleanState = {
  savedRoute: null,
  points: [],
  finishOnly: false,
  pickMode: null,
  itineraryIsVisible: false,
  roundtripParams: {
    distance: 5000,
    seed: 0,
  },
  isochroneParams: {
    distanceLimit: 0,
    timeLimit: 600,
    buckets: 1,
  },
  ...clearResult,
};

export interface RoutePlannerState extends RoutePlannerCleanState {
  transportType: TransportType;
  mode: RoutingMode;
  milestones: 'abs' | 'rel' | false;
}

export const routePlannerInitialState: RoutePlannerState = {
  transportType: 'hiking',
  mode: 'route',
  milestones: false,
  ...cleanState,
};

/** The route as a saved map document stores it; the rest of the slice is result state. */
export type RoutePlannerMapData = Partial<
  Pick<
    RoutePlannerState,
    | 'transportType'
    | 'points'
    | 'finishOnly'
    | 'pickMode'
    | 'mode'
    | 'milestones'
    | 'roundtripParams'
    | 'isochroneParams'
  >
> & {
  /** The computed route, so opening the map needn't ask the router again. */
  result?: SavedRoute;
};

/**
 * A map document's route on top of the slice defaults, carrying the stored
 * result where the document has one that still describes its own waypoints.
 *
 * Shared by the load and by the my-maps unsaved-changes comparison, which needs
 * to know what a document *would* put on screen: if the two disagreed on a
 * single field, every saved map using it would read as changed the moment it was
 * compared against its own document.
 */
export function routePlannerFromMapData(
  data: RoutePlannerMapData | undefined,
): RoutePlannerState {
  const state: RoutePlannerState = {
    ...routePlannerInitialState,
    savedRoute: data?.result ?? null,
    transportType:
      data?.transportType ?? routePlannerInitialState.transportType,
    points: data?.points ?? routePlannerInitialState.points,
    finishOnly: data?.finishOnly ?? routePlannerInitialState.finishOnly,
    pickMode: data?.pickMode ?? routePlannerInitialState.pickMode,
    mode: data?.mode ?? routePlannerInitialState.mode,
    milestones: data?.milestones ?? routePlannerInitialState.milestones,
    roundtripParams:
      data?.roundtripParams ?? routePlannerInitialState.roundtripParams,
    isochroneParams:
      data?.isochroneParams ?? routePlannerInitialState.isochroneParams,
  };

  // A stored route that names other waypoints was left behind by an edit that
  // never reached it. It is kept all the same — putting it on screen is gated on
  // the waypoints it names, so it simply waits for them.
  const route = standingSavedRoute(state);

  return route ? showSavedRoute(state, route) : state;
}

/** The map's stored route, when it answers for the route as it now stands. */
export function standingSavedRoute(
  state: RoutePlannerState,
): SavedRoute | undefined {
  const { savedRoute } = state;

  return savedRoute && savedRoute.key === routeKey(state)
    ? savedRoute
    : undefined;
}

/**
 * Whether the map's stored route is the result on screen, so nothing has to put
 * it there. A route whose result is still the stored one but whose waypoints have
 * since moved is not showing it — that line is merely the last one drawn. The
 * timestamp is compared first, rejecting that case without hashing.
 */
export function storedRouteIsShowing(state: RoutePlannerState): boolean {
  return (
    state.savedRoute?.timestamp === state.timestamp &&
    standingSavedRoute(state) !== undefined
  );
}

/**
 * {@link storedRouteIsShowing} for a component. Memoized on the slice: an inline
 * selector re-runs on every dispatch, and this one hashes the waypoints.
 */
export const storedRouteIsShowingSelector = createSelector(
  [(state: RootState) => state.routePlanner],
  storedRouteIsShowing,
);

function showSavedRoute(
  state: RoutePlannerState,
  route: SavedRoute,
): RoutePlannerState {
  return {
    ...state,
    ...clearResult,
    alternatives: [route.alternative],
    waypoints: route.waypoints,
    timestamp: route.timestamp,
    resultKey: route.key,
    sampledGeojson: route.geometry
      ? { line: route.geometry, saved: true }
      : null,
  };
}

export const routePlannerReducer = createReducer(
  routePlannerInitialState,
  (builder) =>
    builder
      .addCase(routePlannerDelete, (state) => ({
        ...routePlannerInitialState,
        transportType: state.transportType,
        mode: state.mode,
        milestones: state.milestones,
        pickMode: 'start',
      }))
      .addCase(routePlannerToggleMilestones, (state, action) => {
        return {
          ...state,
          milestones:
            action.payload.toggle && state.milestones === action.payload.type
              ? false
              : action.payload.type,
        };
      })
      .addCase(selectFeature, (state, { payload }) => ({
        ...state,
        pickMode:
          payload?.type === 'route-point'
            ? payload.id === 0 && !state.finishOnly
              ? 'start'
              : payload.id === state.points.length - 1
                ? 'finish'
                : null
            : null,
      }))
      // Arm point-picking when route-planner is opened (or restored): fall back
      // to finishing an existing route, or starting a new one. Only when nothing
      // is already armed.
      .addCase(openTool, (state, action) =>
        action.payload === 'route-planner'
          ? {
              ...state,
              pickMode:
                state.pickMode ?? (getStart(state) ? 'finish' : 'start'),
            }
          : state,
      )
      .addCase(clearMapFeatures, (state) => ({
        ...routePlannerInitialState,
        transportType: state.transportType,
        mode: state.mode,
        milestones: state.milestones,
        pickMode: 'start',
      }))
      .addCase(routePlannerSetParams, (state, { payload }) => ({
        ...state,
        ...(payload.points.length === 0
          ? {
              ...routePlannerInitialState,
              transportType: state.transportType,
              mode: state.mode,
            }
          : {}),
        points: payload.points, // TODO mangle - see below old commented code
        finishOnly: payload.finishOnly,
        // start: payload.start,
        // finish:
        //   transportTypeDefs[payload.transportType].api === 'gh' &&
        //   (payload.mode ?? state.mode) !== 'route'
        //     ? null
        //     : payload.finish,
        // midpoints:
        //   transportTypeDefs[payload.transportType].api === 'gh' &&
        //   (payload.mode ?? state.mode) !== 'route'
        //     ? []
        //     : payload.midpoints,
        transportType: payload.transportType,
        mode:
          transportTypeDefs[payload.transportType].api !== 'gh' &&
          payload.mode === 'isochrone'
            ? 'route'
            : payload.mode || 'route',
        milestones: payload.milestones ?? false,
        roundtripParams: {
          ...state.roundtripParams,
          ...payload.roundtripParams,
        },
        isochroneParams: {
          ...state.isochroneParams,
          ...payload.isochroneParams,
        },
        hash: payload.hash,
      }))
      .addCase(routePlannerSetStart, (state, { payload }) => ({
        ...state,
        points:
          (!(
            transportTypeDefs[state.transportType].api === 'gh' &&
            state.mode !== 'route'
          ) &&
            getStart(state)) ||
          state.finishOnly
            ? [
                {
                  transport: state.points[0]?.transport,
                  ...payload,
                },
                ...state.points,
              ]
            : [
                { manual: state.points[0]?.transport, ...payload },
                ...state.points.slice(1),
              ],
        finishOnly: false,
        pickMode: getFinish(state) ? state.pickMode : 'finish',
      }))
      .addCase(routePlannerSetFinish, (state, { payload }) =>
        // only possible in (round)trip mode
        payload === null
          ? {
              ...state,
              points: state.points.slice(0, state.points.length - 1),
              pickMode: getStart(state) ? 'finish' : 'start',
              finishOnly: false,
            }
          : {
              ...state,
              points:
                state.points.length > 1
                  ? [
                      ...state.points.slice(0, -1),
                      {
                        ...state.points.at(-1)!,
                        transport: state.points.at(-2)!.transport,
                      },
                      { ...payload },
                    ]
                  : [...state.points, { ...payload }],
              pickMode: getStart(state) ? 'finish' : 'start',
              finishOnly: state.points.length === 0,
            },
      )
      .addCase(routePlannerSwapEnds, (state) => {
        state.points.reverse();

        for (let i = 1; i < state.points.length; i++) {
          state.points[i - 1].transport = state.points[i].transport;
        }

        delete state.points.at(-1)?.transport;
      })
      .addCase(
        routePlannerAddPoint,
        (state, { payload: { position, point: midpoint } }) => {
          state.points.splice(position + 1, 0, {
            ...midpoint,
            transport: midpoint.transport ?? state.points[position]?.transport,
          });
        },
      )
      .addCase(routePlannerSetPoint, (state, action) => {
        state.points[action.payload.position] = action.payload.point;
      })
      .addCase(routePlannerSetPoints, (state, { payload }) => {
        state.points = payload;
      })
      .addCase(routePlannerRemovePoint, (state, action) => {
        state.points.splice(action.payload, 1);
      })
      .addCase(
        routePlannerSetTransportType,
        (state, { payload: transportType }) => ({
          ...state,
          ...clearResult,
          transportType,
          mode:
            transportTypeDefs[transportType].api ===
            transportTypeDefs[state.transportType].api
              ? state.mode
              : 'route',
        }),
      )
      .addCase(routePlannerSetMode, (state, { payload: mode }) => {
        if (
          transportTypeDefs[state.transportType].api === 'gh' &&
          mode !== 'route'
        ) {
          state.finishOnly = false;

          state.points.splice(1);
        }

        state.mode =
          transportTypeDefs[state.transportType].api !== 'gh' &&
          mode === 'isochrone'
            ? 'route'
            : mode;
      })
      .addCase(routePlannerSetPickMode, (state, action) => ({
        ...state,
        pickMode: action.payload,
      }))
      .addCase(routePlannerToggleItineraryVisibility, (state) => ({
        ...state,
        itineraryIsVisible: !state.itineraryIsVisible,
      }))
      .addCase(
        routePlannerSetResult,
        (state, { payload: { alternatives, waypoints, timestamp, key } }) => ({
          ...state,
          ...clearResult,
          alternatives,
          waypoints,
          timestamp,
          resultKey: key,
          activeAlternativeIndex: 0,
        }),
      )
      .addCase(
        routePlannerSetIsochrones,
        (state, { payload: { isochrones, timestamp } }) => ({
          ...state,
          ...clearResult,
          isochrones,
          timestamp,
          midpoints: [],
        }),
      )
      .addCase(routePlannerSetActiveAlternativeIndex, (state, action) => ({
        ...state,
        activeAlternativeIndex: action.payload,
        // A different alternative needs its own elevation line.
        sampledGeojson: null,
        renderGeojson: null,
      }))
      .addCase(routePlannerSetSampledGeojson, (state, action) => {
        state.sampledGeojson = { line: action.payload, saved: false };
      })
      .addCase(routePlannerSetSavedRoute, (state, action) => {
        state.savedRoute = action.payload;
      })
      .addCase(routePlannerRestoreSavedRoute, (state) => {
        const route = standingSavedRoute(state);

        return route ? showSavedRoute(state, route) : state;
      })
      // Clearing the result is what lets the request through: the stored route is
      // no longer on screen, so nothing short-circuits it. The route itself is
      // kept — a recompute that fails puts it straight back.
      .addCase(routePlannerRecompute, (state) => ({
        ...state,
        ...clearResult,
      }))
      .addCase(routePlannerSupersedeSavedRoute, (state) => {
        state.savedRoute = null;
      })
      // The map it belongs to is gone.
      .addCase(mapsDisconnect, (state) => {
        state.savedRoute = null;
      })
      .addCase(routePlannerSetRenderGeojson, (state, action) => {
        state.renderGeojson = action.payload;
      })
      .addCase(authSetUser, (state) => {
        // The line is sampled differently for premium (every vertex from the
        // terrain model, then densified), so it belongs to the user it was
        // built for. One that came with a saved map belongs to the map instead.
        if (!state.sampledGeojson?.saved) {
          state.sampledGeojson = null;
        }

        state.renderGeojson = null;
      })
      .addCase(elevationSetSettings, (state, { payload }) => {
        // The render line is derived from the smoothing windows, and from
        // nothing else in the slice — the steepness window is read off the
        // drawn points, so dropping the cache for it would resample for nothing.
        // The sampled line it is built from stands, so this costs no request.
        if (affectsElevationSmoothing(payload)) {
          state.renderGeojson = null;
        }
      })
      .addCase(routePlannerSetRoundtripParams, (state, { payload }) => ({
        ...state,
        roundtripParams: {
          ...state.roundtripParams,
          ...payload,
        },
      }))
      .addCase(routePlannerSetIsochroneParams, (state, { payload }) => ({
        ...state,
        isochroneParams: {
          ...state.isochroneParams,
          ...payload,
        },
      }))
      .addCase(
        mapsLoaded,
        (
          _state,
          {
            payload: {
              data: { routePlanner },
            },
          },
        ) => routePlannerFromMapData(routePlanner),
      ),
);

/**
 * Whether waypoint-order optimization is offered: GraphHopper point-to-point
 * routing with at least one movable midpoint. Shared by the menu (to show the
 * section) and the processor (to guard direct dispatches).
 */
export function routePlannerOptimizeApplicable(
  state: RoutePlannerState,
): boolean {
  return (
    transportTypeDefs[state.transportType].api === 'gh' &&
    state.mode === 'route' &&
    state.points.length >= 3
  );
}

/**
 * Whether any waypoint overrides the transport (a multimodal route). Reordering
 * under a single profile would scramble per-leg transports, so optimization is
 * blocked for these.
 */
export function routePlannerHasTransportOverride(
  state: RoutePlannerState,
): boolean {
  return state.points.some((point) => point.transport != null);
}

export function getStart(state: RoutePlannerState) {
  return state.finishOnly ? null : state.points[0];
}

export function getFinish(state: RoutePlannerState) {
  return !state.finishOnly && state.points.length < 2
    ? undefined
    : state.points.at(-1);
}
